import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { FaEdit, FaTimes } from 'react-icons/fa';
import SearchAllocation from '../allocation/SearchAllocation';
import './ComplaintManagement.css';

const AddComplaint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPgMode = location.pathname.startsWith('/pg/');

  const [formData, setFormData] = useState({
    allocation_id: '',
    complaint_type: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(isPgMode);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [showAllocationSelector, setShowAllocationSelector] = useState(false);
  const [complaintTypes, setComplaintTypes] = useState([]);

  useEffect(() => {
    if (isPgMode) {
      loadPgContext();
    } else {
      fetchComplaintTypes();
    }
  }, [isPgMode]);

  const fetchComplaintTypes = async () => {
    try {
      const response = await apiClient.get('/complaints/types');
      const types = response.data || [];
      setComplaintTypes(types.map((type) => type.type_name || type));
    } catch (error) {
      console.error('Error fetching complaint types:', error);
      setComplaintTypes([
        'Maintenance Issue',
        'Cleanliness',
        'Noise Complaint',
        'Facility Issue',
        'Staff Behavior',
        'Safety Concern',
        'Food Quality',
        'Room Issue',
        'Internet/WiFi',
        'Electricity',
        'Water Supply',
        'Other',
      ]);
    }
  };

  const loadPgContext = async () => {
    setBootLoading(true);
    try {
      const [allocRes, typesRes] = await Promise.all([
        apiClient.get('/pg/me/allocation'),
        apiClient.get('/pg/me/complaint-types'),
      ]);
      const allocation = allocRes.data;
      if (!allocation?.allocation_id) {
        toastError(
          'No active bed allocation. Please contact hostel management.',
        );
        navigate('/pg/complaints');
        return;
      }
      setSelectedAllocation(allocation);
      setFormData((prev) => ({
        ...prev,
        allocation_id: allocation.allocation_id,
      }));
      const types = typesRes.data || [];
      setComplaintTypes(
        types.map((type) => type.type_name || type).filter(Boolean),
      );
    } catch (error) {
      toastError(
        error.response?.data?.detail || 'Unable to load complaint form.',
      );
      navigate('/pg/complaints');
    } finally {
      setBootLoading(false);
    }
  };

  const handleAllocationSelect = (allocation) => {
    setSelectedAllocation(allocation);
    setFormData((prev) => ({
      ...prev,
      allocation_id: allocation.allocation_id,
    }));
    setShowAllocationSelector(false);

    if (errors.allocation_id) {
      setErrors((prev) => ({
        ...prev,
        allocation_id: undefined,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const clearSelection = () => {
    setSelectedAllocation(null);
    setFormData((prev) => ({
      ...prev,
      allocation_id: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.allocation_id) {
      newErrors.allocation_id = 'Please select a bed allocation';
    }

    if (!formData.complaint_type) {
      newErrors.complaint_type = 'Please select a complaint type';
    }

    if (isPgMode && !formData.description.trim()) {
      newErrors.description = 'Please describe the issue';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      if (isPgMode) {
        const response = await apiClient.post('/pg/me/complaints', {
          complaint_type: formData.complaint_type,
          description: formData.description.trim(),
        });
        if (response.status === 201 || response.status === 200) {
          toastSuccess(
            response.data?.message || 'Complaint submitted successfully!',
          );
          navigate('/pg/complaints');
        } else {
          toastError('Failed to submit complaint');
        }
      } else {
        const complaintData = {
          allocation_id: parseInt(formData.allocation_id),
          complaint_type: formData.complaint_type,
          description: formData.description.trim(),
        };

        const response = await apiClient.post('/complaints', complaintData);

        if (response.status === 201 || response.status === 200) {
          toastSuccess('Complaint submitted successfully!');
          navigate('/complaints');
        } else {
          toastError('Failed to submit complaint');
        }
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      const errorMessage =
        error.response?.data?.detail || 'Failed to submit complaint';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (isPgMode) {
      setFormData((prev) => ({
        ...prev,
        complaint_type: '',
        description: '',
      }));
    } else {
      setFormData({
        allocation_id: '',
        complaint_type: '',
        description: '',
      });
      setSelectedAllocation(null);
    }
    setErrors({});
  };

  if (bootLoading) {
    return (
      <RTPageLayout activePath={location.pathname} mainSection>
        <RTSpinner message="Loading…" fullScreen={false} />
      </RTPageLayout>
    );
  }

  if (!isPgMode && showAllocationSelector) {
    return (
      <RTPageLayout
        title="Select Bed Allocation"
        actions={
          <RTButton
            variant="secondary"
            onClick={() => setShowAllocationSelector(false)}
          >
            ← Back
          </RTButton>
        }
        mainSection
        activePath={location.pathname}
      >
        <SearchAllocation mode="Select" onSelect={handleAllocationSelect} />
      </RTPageLayout>
    );
  }

  const backButton = (
    <RTButton variant="secondary" onClick={() => navigate(-1)}>
      ← Back
    </RTButton>
  );

  return (
    <RTPageLayout
      title={isPgMode ? 'Raise Complaint' : 'Add Complaint'}
      actions={backButton}
      mainSection
      activePath={location.pathname}
    >
      <form
        onSubmit={handleSubmit}
        className="complaint-form complaint-form--compact"
      >
        <div className="complaint-form-sections">
          <div className="complaint-section">
            <h3 className="complaint-section-title">Complaint Details</h3>

            <div className="complaint-form-group">
              <label>
                Bed Allocation <span style={{ color: 'red' }}>*</span>
              </label>
              <div
                className={`selection-container selection-container--compact${selectedAllocation ? ' is-selected' : ''}${isPgMode ? ' is-locked' : ''}`}
                onClick={
                  isPgMode ? undefined : () => setShowAllocationSelector(true)
                }
                role={isPgMode ? undefined : 'button'}
                tabIndex={isPgMode ? undefined : 0}
                onKeyPress={
                  isPgMode
                    ? undefined
                    : (e) => {
                        if (e.key === 'Enter') setShowAllocationSelector(true);
                      }
                }
                style={isPgMode ? { cursor: 'default' } : undefined}
              >
                {selectedAllocation ? (
                  <div className="selection-filled">
                    <div className="selected-info">
                      <div className="selected-name">
                        {selectedAllocation.pg_name}
                        <span className="selected-meta">
                          {' '}
                          · {selectedAllocation.building_name}
                          {selectedAllocation.room_name
                            ? ` · ${selectedAllocation.room_name}`
                            : ''}
                          {selectedAllocation.pg_mobile
                            ? ` · ${selectedAllocation.pg_mobile}`
                            : ''}
                        </span>
                      </div>
                    </div>
                    {!isPgMode ? (
                      <div className="selection-actions">
                        <button
                          type="button"
                          className="icon-button edit-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllocationSelector(true);
                          }}
                          title="Change Selection"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="icon-button clear-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearSelection();
                          }}
                          title="Clear Selection"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="selection-empty">
                    {isPgMode ? 'Loading allocation…' : 'Select bed allocation'}
                  </div>
                )}
              </div>
              {errors.allocation_id && (
                <span className="form-error">{errors.allocation_id}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>
                Complaint Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                name="complaint_type"
                value={formData.complaint_type}
                onChange={handleInputChange}
              >
                <option value="">Select complaint type</option>
                {complaintTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.complaint_type && (
                <span className="form-error">{errors.complaint_type}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>
                Description
                {isPgMode ? <span style={{ color: 'red' }}> *</span> : null}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={
                  isPgMode
                    ? 'Describe the issue…'
                    : 'Optional details about the complaint...'
                }
                rows="4"
              />
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}
              >
                {formData.description.length}/500 characters
              </div>
              {errors.description && (
                <span className="form-error">{errors.description}</span>
              )}
            </div>
          </div>
        </div>

        <div className="complaint-actions">
          {!isPgMode ? (
            <RTButton
              type="button"
              variant="secondary"
              onClick={resetForm}
              disabled={loading}
            >
              Reset
            </RTButton>
          ) : null}
          <RTButton type="submit" variant="primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Complaint'}
          </RTButton>
        </div>
      </form>

      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <RTSpinner
            size="medium"
            showMessage={true}
            message="Processing your complaint..."
          />
        </div>
      )}
    </RTPageLayout>
  );
};

export default AddComplaint;
